<script setup lang="ts">
import {ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useStorage} from "@vueuse/core";
import AutoComplete from "primevue/autocomplete";
import {getGroup, storeGroup, updateGroup} from "@/Services/Admin/admin-groups";
import {getUsers as apiGetUsers} from "@/Services/Admin/admin-users";
import {saving} from "@/Composables/api";
import {Button, Card, ColorPicker, FileUpload, InputText, Label, Textarea} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import {AnonymizationSelect, FormButtons, Messages} from "@/Components/Forms";
import {deleteIcon as deleteIconViaApi} from "../../../Services/groups";

const bag = "group-admin-form";
const currentRoute = useRoute();
const router = useRouter();

const defaultForm = {
    id: null,
    name: null,
    description: null,
    anonymization: null,
    color: null,
    user: null,
    tags: [],
};

const group = useStorage(bag, defaultForm);
const users = ref([]);
const groupIconUploadRef = ref<typeof FileUpload>();

const getUsers = async (searchQuery?: string) => {
    apiGetUsers({
        filter: {
            search: searchQuery,
        },
    }).then(({data}) => (users.value = data));
};

if (currentRoute.params.group) {
    getGroup(currentRoute.params.group).then(({data}) => {
        Object.assign(group.value, data);
        group.value.color = data.color?.slug;
        group.value.anonymization = data.anonymization?.slug || "full_name";
    });
} else if (group.value.id) {
    group.value = defaultForm;
}

const save = async () => {
    let postData = {
        id: group.value.id,
        name: group.value.name,
        description: group.value.description,
        anonymization: group.value.anonymization,
        color: group.value.color,
        user_id: group.value.user.id,
        tags: group.value.tags.map((tag) => tag.name),
    };
    if (currentRoute.params.group) {
        await updateGroup(currentRoute.params.group, postData);
    } else {
        await storeGroup(postData).then(({data}) => {
            Object.assign(group.value, data);
        });
    }

    await groupIconUploadRef.value?.startUpload();

    router.push({name: "admin.groups.show", params: {group: group.value.id}});
};

const deleteIcon = async () => {
    await deleteIconViaApi(group.value.id, group.value.icon.id);
    group.value.icon = null;
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-6">
        <div class="m-container mb-3">
            <PageHeading :title="`${currentRoute.params.group ? 'Edit' : 'Create'} Group`" />
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #title>Owner</template>
                <div class="flex flex-col">
                    <Label for="user_id" class="mb-1" required>User</Label>
                    <AutoComplete
                        input-id="user_id"
                        name="group"
                        v-model="group.user"
                        :suggestions="users"
                        optionLabel="name"
                        optionValue="slug"
                        placeholder="Start typing to search for a user"
                        required
                        fluid
                        @complete="getUsers($event.query)"
                    />
                </div>
            </Card>
            <Card>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col">
                        <Label for="name" class="mb-1" required>Name</Label>
                        <InputText id="name" name="name" v-model="group.name" required fluid />
                    </div>
                    <div class="flex flex-col">
                        <Label for="description" class="mb-1">Description</Label>
                        <Textarea id="description" name="description" v-model="group.description" fluid auto-resize />
                    </div>
                </div>
            </Card>
            <Card>
                <div class="mb--6">
                    <h3 class="mb-2 font-bold">Anonymization</h3>
                    <AnonymizationSelect v-model="group.anonymization" />
                </div>
            </Card>
            <Card>
                <div class="mb--6">
                    <h3 class="mb-2 font-bold">Icon & color</h3>
                    <div class="mb-2 flex flex-col">
                        <div
                            :key="group.icon?.url ?? 0"
                            v-if="group.icon?.url"
                            class="relative mx-auto mb-3 aspect-square max-h-48 max-w-48"
                        >
                            <Button
                                class="!absolute right-0 top-0 z-20 !p-1 text-red-600 hover:bg-red-600 hover:text-white"
                                :icon="{
                                    icon: 'mdi:close',
                                    width: '20',
                                }"
                                @click.stop="deleteIcon"
                            />
                            <div class="h-full w-full overflow-hidden rounded-xl">
                                <img
                                    :src="group.icon?.url"
                                    :alt="`${group.name} group icon`"
                                    class="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <FileUpload
                            ref="groupIconUploadRef"
                            collection="icon"
                            :parent-id="group.id"
                            parent-type="group"
                        />
                    </div>
                    <ColorPicker v-model="group.color" />
                </div>
            </Card>
            <!--            <Card>-->
            <!--                <TagSelect v-model="group.tags" />-->
            <!--            </Card>-->
            <Messages :bag="bag" />
            <FormButtons :loading="!!saving" :show-cancel="false" />
        </form>
    </div>
</template>
<style scoped></style>
